// frontend/src/services/auth.js
import { API_BASE } from "../config/api";
import { httpRequest } from "../utils/httpClient";
import logger from '../utils/logger';

// In-memory token cache only. Tokens are no longer persisted in localStorage or
// Capacitor Preferences. On web, requests rely on the httpOnly cookie set by the
// backend. On mobile, the token lives only in memory for the current app session.
let _cachedToken = null;
let _cachedExpires = null;

function _syncTokenToLocal(token, expires) {
  if (token) {
    _cachedToken = token;
  }
  if (expires) {
    _cachedExpires = expires;
  }
}

function _clearLocalToken() {
  _cachedToken = null;
  _cachedExpires = null;
}

export const authService = {
  // Read token from in-memory cache only. Web requests rely on the httpOnly cookie.
  getToken() {
    return _cachedToken;
  },

  // Tokens are no longer persisted cross-session. Returning null forces reliance
  // on the httpOnly cookie (web) or a fresh login (mobile).
  async restoreFromCapacitor() {
    return null;
  },

  setToken(token, rememberMe = false) {
    const expires = rememberMe ?
      (Date.now() + 30 * 24 * 60 * 60 * 1000).toString() :
      (Date.now() + 7 * 24 * 60 * 60 * 1000).toString();

    // Keep token only in memory for the current session
    _syncTokenToLocal(token, expires);
  },

  isTokenExpired() {
    const expires = _cachedExpires;
    if (!expires) return true;
    return Date.now() > parseInt(expires, 10);
  },

  removeToken() {
    // Always remove the in-memory token
    _clearLocalToken();

    // Clear Quick Start sessionStorage flags so modal shows on next login
    try {
      sessionStorage.removeItem("quick_start_shown_this_session");
      sessionStorage.removeItem("quick_start_state");
    } catch (e) {
      // Ignore sessionStorage errors
    }
  },

  getAuthHeaders() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  async register(email, password, rememberMe = false, legalVersion = null) {
    const payload = { email, password };
    if (legalVersion) {
      payload.legal_version = legalVersion;
    }
    const res = await httpRequest(`${API_BASE}/auth/email/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, remember_me: rememberMe }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Registration failed");
    }
    const data = await res.json();
    // Email/password registrations now require verification before full login.
    if (data.access_token) {
      await this.setToken(data.access_token, rememberMe);
    }
    return data;
  },

  async login(email, password, rememberMe = false) {
    const loginUrl = `${API_BASE}/auth/email/login`;
    const requestHeaders = { "Content-Type": "application/json" };
    const requestBody = JSON.stringify({ email, password, remember_me: rememberMe });


    try {
      const res = await httpRequest(loginUrl, {
        method: "POST",
        headers: requestHeaders,
        body: requestBody,
      });


      if (!res.ok) {
        let errorData;
        try {
          errorData = await res.json();
        } catch (e) {
          errorData = { detail: `HTTP ${res.status}: ${res.statusText}` };
        }
                throw new Error(errorData.detail || "Login failed");
      }
      const data = await res.json();
      if (data.access_token) {
        await this.setToken(data.access_token, rememberMe);
      }
      return data;
    } catch (err) {
            throw err;
    }
  },

  async oauthCallback(provider, token, email = null, name = null, rememberMe = false) {
    const url = `${API_BASE}/auth/oauth/callback`;

    try {
      const res = await httpRequest(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          token,
          email,
          name,
          remember_me: rememberMe,
        }),
      });


      if (!res.ok) {
        let errorData;
        try {
          errorData = await res.json();
        } catch (e) {
          errorData = { detail: `HTTP ${res.status}: ${res.statusText}` };
        }
                throw new Error(errorData.detail || "OAuth authentication failed");
      }

      const data = await res.json();
            await this.setToken(data.access_token, rememberMe);
      return data;
    } catch (err) {

      // Handle network errors (Failed to Fetch or CapacitorHttp network errors)
      if (err.message === "Failed to fetch" || err.name === "TypeError" || err.message.includes("fetch") || err.isNetworkError) {
        logger.error(`Network error connecting to backend at ${url}`);
        logger.error("Make sure the backend is running on", API_BASE);
        throw new Error(
          `Cannot connect to backend server at ${API_BASE}. ` +
          `Please ensure the backend is running and accessible. ` +
          `For local development, start the backend with: python -m uvicorn backend.main:app --reload`
        );
      }
      // Re-throw other errors
      throw err;
    }
  },

  async getCurrentUser() {
    const res = await httpRequest(`${API_BASE}/auth/me`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) {
      return null;
    }
    return await res.json();
  },

  async resendVerificationEmail(email) {
    const res = await httpRequest(`${API_BASE}/auth/email/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to resend verification email");
    }
    return await res.json();
  },

  async requestPasswordReset(email) {
    const res = await httpRequest(`${API_BASE}/auth/password/forgot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to send password reset email");
    }
    return await res.json();
  },

  async resetPassword(token, password) {
    const res = await httpRequest(`${API_BASE}/auth/password/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to reset password");
    }
    return await res.json();
  },

  async verifyEmailToken(token) {
    const res = await httpRequest(`${API_BASE}/auth/email/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Email verification failed");
    }
    const data = await res.json();
    if (data.access_token) {
      await this.setToken(data.access_token);
    }
    return data;
  },

  async getAuthStatus() {
    const res = await httpRequest(`${API_BASE}/auth/status`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) {
      return { is_authenticated: false, is_email_verified: false };
    }
    return await res.json();
  },

  async updateUsername(username) {
    const res = await httpRequest(`${API_BASE}/profile/username`, {
      method: "PUT",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update username");
    }
    return await res.json();
  },

  async updateBggId(bgg_id) {
    const res = await httpRequest(`${API_BASE}/profile/bgg-id`, {
      method: "PUT",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ bgg_id }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update BGG ID");
    }
    return await res.json();
  },

  async updateCountry(country_of_domicile) {
    const res = await httpRequest(`${API_BASE}/profile/country`, {
      method: "PUT",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ country_of_domicile }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update country");
    }
    return await res.json();
  },

  async updatePersona(persona_title, persona_icon) {
    const res = await httpRequest(`${API_BASE}/auth/persona`, {
      method: "PATCH",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ persona_title, persona_icon }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to save persona");
    }
    return await res.json();
  },

  async deleteAccount() {
    const res = await httpRequest(`${API_BASE}/profile/account`, {
      method: "DELETE",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to request account deletion");
    }
    return await res.json();
  },

  async reactivateAccount(token) {
    const res = await httpRequest(`${API_BASE}/profile/account/reactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to reactivate account");
    }
    const data = await res.json();
    if (data.access_token) {
      await this.setToken(data.access_token);
    }
    return data;
  },

  async logout() {
    try {
      await httpRequest(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      logger.error("[Auth] Backend logout failed:", error);
    }
    this.removeToken();

    // Clear Quick Start sessionStorage flags so modal shows on next login
    try {
      sessionStorage.removeItem("quick_start_shown_this_session");
      sessionStorage.removeItem("quick_start_state");
      logger.info("[Auth] Cleared Quick Start sessionStorage flags on logout");
    } catch (e) {
      // Ignore sessionStorage errors
    }
  },
};
